import React, { useState } from 'react';
import { Button } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { useDebugPanelConfig } from '../config';
import {
  mockTcapiAgencyId,
  simulateTcapiCall,
  simulateTcapiMessage,
} from '../services/els';
import {
  StyledDescription,
  StyledError,
  StyledSuccess,
} from './TextTo911DebugPanel.styles';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface SimulateCallControlsProps {
  phoneNumber: string;
  callId: string | null;
}

/**
 * Controls for simulating a text-to-911 session. Triggers Shanghai's mock
 * TCAPI server to emit a callPresented event, which creates the TCAPI session
 * and puts the call in the queue. When a TCAPI call is selected in the queue,
 * shows a message input that sends simulated inbound messages from the caller
 * via Shanghai's simulate-message endpoint.
 */
export function SimulateCallControls({
  phoneNumber,
  callId,
}: SimulateCallControlsProps) {
  const { accountId } = useDebugPanelConfig();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [messageText, setMessageText] = useState('');
  const [messageStatus, setMessageStatus] = useState<Status>('idle');
  const [messageError, setMessageError] = useState('');

  async function handleSimulate() {
    setStatus('sending');
    setErrorMessage('');

    try {
      // TODO: The mockTcapiAgencyId convention only works for environments
      //   where we have TCAPI mocked with shanghai. We'll need to dynamically
      //   fetch or configure the agency ID for other environments (staging,
      //   standbox, and prod).
      // The agency ID follows the deterministic convention from
      // mockTcapiAgencyId() so Shanghai routes the callPresented event
      // only to this authority's WebSocket connection.
      await simulateTcapiCall(phoneNumber, mockTcapiAgencyId(accountId));
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  async function handleSendMessage() {
    if (!callId || !messageText.trim()) return;

    setMessageStatus('sending');
    setMessageError('');

    try {
      await simulateTcapiMessage(callId, messageText.trim());
      setMessageStatus('sent');
      setMessageText('');
      setTimeout(() => setMessageStatus('idle'), 2000);
    } catch (err) {
      setMessageStatus('error');
      setMessageError(
        err instanceof Error ? err.message : 'Failed to send message',
      );
    }
  }

  return (
    <div>
      <Button
        onClick={handleSimulate}
        btnType="primary"
        disabled={status === 'sending'}
      >
        <strong>
          {status === 'sending' ? 'Sending...' : 'Simulate Text-to-911 Session'}
        </strong>
      </Button>
      {status === 'sent' && (
        <StyledSuccess>
          Text-to-911 session simulated via Shanghai mock TCAPI. The call should
          appear in the queue shortly.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
      {callId && (
        <div style={{ marginTop: '12px' }}>
          <StyledDescription>
            Simulate an inbound message from the caller:
          </StyledDescription>
          <div
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' && messageText.trim()) {
                handleSendMessage();
              }
            }}
          >
            <div style={{ flex: 1 }}>
              <InputText
                type="text"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setMessageText(event.target.value)
                }
                value={messageText}
                placeholder="Type a message from the caller..."
              />
            </div>
            <Button
              onClick={handleSendMessage}
              btnType="secondary"
              disabled={messageStatus === 'sending' || !messageText.trim()}
            >
              <strong>
                {messageStatus === 'sending' ? 'Sending...' : 'Send'}
              </strong>
            </Button>
          </div>
          {messageStatus === 'sent' && (
            <StyledSuccess>Message sent.</StyledSuccess>
          )}
          {messageStatus === 'error' && (
            <StyledError>Error: {messageError}</StyledError>
          )}
        </div>
      )}
    </div>
  );
}
